import {
  useSalesServiceSalesOrdersMediaCreate,
  useSalesServiceSalesOrdersMediaListKey
} from '@insurance/api/queries'
import {
  OrderMediaCreate,
  OrderMediaDetail as OrderMediaGet
} from '@insurance/api/requests'
import { Text } from '@shared/components'
import { WithPagination } from '@shared/types'
import { replaceEmptyWithUndefined, showApiErrors } from '@shared/utils'
import { useQueryClient } from '@tanstack/react-query'
import { Flex, Input, Modal, Spin, UploadFile } from 'antd'
import Dragger from 'antd/es/upload/Dragger'
import { DocumentUpload } from 'iconsax-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  orderId?: string | number
  onDisabledFileGet?: (disabled: boolean) => void
  onSuccess?: () => void
}

export const AddOrderMediaFields = ({
  orderId,
  onDisabledFileGet,
  onSuccess
}: Props) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [notes, setNotes] = useState('')
  const [files, setFiles] = useState<UploadFile[]>([])
  const [openModal, setOpenModal] = useState(false)

  const onCloseAddMedia = () => {
    setNotes('')
    setFiles([])
    setOpenModal(false)
  }

  useEffect(() => {
    onDisabledFileGet?.(!!files.length)
  }, [!!files.length])

  const { mutateAsync: addMedia, isPending: isAddMediaPending } =
    useSalesServiceSalesOrdersMediaCreate({
      onSuccess: (data, variable) => {
        let orderMediaList: OrderMediaGet[] = []

        queryClient.setQueryData(
          [useSalesServiceSalesOrdersMediaListKey, { order: Number(orderId) }],
          (oldData: WithPagination<OrderMediaGet>) => {
            const results = oldData?.results || []
            const count = oldData?.count || 0

            orderMediaList = [
              ...results,
              {
                ...data,
                media: {
                  id: Number(data?.media),
                  file: {
                    headers: {},
                    url: window.URL.createObjectURL(
                      variable?.formData?.file as unknown as Blob
                    )
                  },
                  name: variable?.formData?.name
                }
              } as unknown as OrderMediaGet
            ]

            return {
              ...oldData,
              count: count + 1,
              results: orderMediaList
            } as WithPagination<OrderMediaGet>
          }
        )
      },
      onError: (error) => showApiErrors(error)
    })

  const onAddMedia = async () => {
    if (!orderId || !files.length) return

    setOpenModal(false)

    await Promise.all(
      files.map((file) => {
        return addMedia({
          formData: {
            file: file.originFileObj as unknown as string,
            ...replaceEmptyWithUndefined({
              order: Number(orderId),
              name: file.name,
              notes
            })
          } as OrderMediaCreate
        })
      })
    )

    onCloseAddMedia()
    onSuccess?.()
  }

  return (
    <>
      <Spin spinning={isAddMediaPending}>
        <Dragger
          multiple
          onChange={({ fileList }) => {
            setFiles(fileList)
            setOpenModal(true)
          }}
          fileList={[]}
          customRequest={({ onSuccess }) => onSuccess?.({})}
        >
          <Flex vertical gap={10}>
            <Flex justify="center" align="center" gap={10}>
              <DocumentUpload size={20} />

              <Text>{t('upload_files')}</Text>
            </Flex>

            <Text>{t('upload_files_text')}</Text>
          </Flex>
        </Dragger>
      </Spin>

      <Modal
        title={t('notes')}
        open={openModal}
        okText={t('save')}
        okButtonProps={{ loading: isAddMediaPending }}
        onCancel={onCloseAddMedia}
        onOk={onAddMedia}
      >
        <Input.TextArea
          value={notes}
          placeholder={t('notes')}
          autoSize={{ minRows: 4 }}
          onChange={(e) => setNotes(e.target.value)}
        />
      </Modal>
    </>
  )
}
